// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { testEntraIdAuthentication } from '@api/portalServices';
import type {
  EntraIdAuthenticationTestResponse,
  UserIdentity,
} from '@api/types';
import { ErrorCode } from '@errorHandler/constants';
import type { ApiError } from '@errorHandler/errors';
import { Modal } from '@imprivata-cloud/components';
import { useMutation } from '@tanstack/react-query';
import { Alert, Collapse, Form, Input } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import CopyButton from '../../../../components/copyButton/copyButton';
import MarkdownMessage from './MarkdownMessage';
import './TestEntraAuthModal.less';

const TRANSLATION_ROOT = 'portal.test-entra-id';

type Props = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  hasSyncedUsers: boolean;
};

type FormValues = { providedName: string; password: string };

const EMPTY = '—';

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

// Microsoft's trace identifier arrives in the untyped rawOutput bag under one
// of two keys depending on the failure path.
const getMicrosoftTrace = (response: EntraIdAuthenticationTestResponse): string =>
  asString(response.results.rawOutput?.trace_id) ||
  asString(response.results.rawOutput?.correlation_id) ||
  EMPTY;

const identityRows = (
  identity: UserIdentity | undefined,
  t: ReturnType<typeof useTranslation>['t'],
): Array<[string, string]> => [
  [t(`${TRANSLATION_ROOT}.identity.entra-upn`), identity?.entraIdUpn || EMPTY],
  [t(`${TRANSLATION_ROOT}.identity.ad-upn`), identity?.adUpn || EMPTY],
  [t(`${TRANSLATION_ROOT}.identity.sam`), identity?.samAccountName || EMPTY],
  [t(`${TRANSLATION_ROOT}.identity.email`), identity?.emailAddress || EMPTY],
  [t(`${TRANSLATION_ROOT}.identity.entra-object-id`), identity?.entraIdId || EMPTY],
  [t(`${TRANSLATION_ROOT}.identity.impr-user-id`), identity?.id || EMPTY],
];

const buildClipboardText = (
  response: EntraIdAuthenticationTestResponse,
  tenantId: string,
  isFailure: boolean,
  t: ReturnType<typeof useTranslation>['t'],
): string => {
  const tr = (key: string) => t(`${TRANSLATION_ROOT}.${key}`);
  const microsoftTrace = getMicrosoftTrace(response);
  const rows = [
    `${tr('clipboard.status')}: ${response.status}`,
    `${tr('clipboard.code')}: ${response.results.code}`,
    `${tr('clipboard.message')}:`,
    response.results.message,
    '',
    `${tr('trace.tenant')}: ${tenantId}`,
    `${tr('trace.impr')}: ${response.traceId}`,
    // The Microsoft trace ID row is only shown on failure; mirror that here.
    ...(isFailure ? [`${tr('trace.ms')}: ${microsoftTrace}`] : []),
    '',
    ...identityRows(response.userIdentity, t).map(([k, v]) => `${k}: ${v}`),
  ];
  if (isFailure && response.results.rawOutput) {
    rows.push('', `${tr('clipboard.raw')}:`);
    rows.push(JSON.stringify(response.results.rawOutput, null, 2));
  }
  return rows.join('\n');
};

// Clipboard text for the "test could not be performed" (4xx/5xx) path, which
// has only the ApiError to work with — mirror the fields shown in the alert.
const buildErrorClipboardText = (
  error: ApiError,
  traceId: string,
  t: ReturnType<typeof useTranslation>['t'],
): string => {
  const tr = (key: string) => t(`${TRANSLATION_ROOT}.errors.${key}`);
  return [
    `${tr('trace-id-label')}: ${traceId}`,
    `${tr('type-label')}: ${error.type ?? EMPTY}`,
    `${tr('code-label')}: ${error.code ?? EMPTY}`,
    `${tr('message-label')}: ${error.message ?? EMPTY}`,
  ].join('\n');
};

const TestEntraAuthModal = ({ open, onClose, tenantId, hasSyncedUsers }: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();

  const mutation = useMutation<EntraIdAuthenticationTestResponse, ApiError, FormValues>({
    mutationFn: ({ providedName, password }) =>
      testEntraIdAuthentication(providedName.trim(), password, tenantId),
  });

  const handleClose = () => {
    onClose();
  };

  // Clear the form store and any prior result once the modal has finished
  // closing, so the next open starts fresh. antd's afterClose is the supported
  // hook for post-close cleanup; the form instance persists via useForm even
  // though destroyOnHidden unmounts the fields, so resetFields clears it.
  const handleAfterClose = () => {
    form.resetFields();
    mutation.reset();
  };

  const handleSubmit = (values: FormValues) => {
    // Drop any prior result/error before firing so the previous panel doesn't
    // linger while the new request is in flight.
    mutation.reset();
    mutation.mutate(values);
  };

  // Disable the Test button until both fields are non-empty.
  const providedName = useWatch('providedName', form);
  const password = useWatch('password', form);
  const canSubmit = Boolean(providedName?.trim()) && Boolean(password);

  // The Test action lives in the Modal footer, outside the <Form>, so native
  // Enter-to-submit never fires. Mirror the footer button here — submit on
  // Enter only when the button would be enabled (both fields filled and no
  // request already in flight), matching the footer button's guards so Enter
  // can't fire a duplicate test while one is pending.
  const handlePressEnter = () => {
    if (canSubmit && !mutation.isPending) {
      form.submit();
    }
  };

  if (!hasSyncedUsers) {
    return (
      <Modal
        destroyOnHidden
        open={open}
        title={t(`${TRANSLATION_ROOT}.title-no-users`)}
        onCancel={handleClose}
        onOk={handleClose}
        okText={t('common.close')}
        cancelButtonProps={{ style: { display: 'none' } }}
        maskClosable={false}
        width={800}
      >
        <p>{t(`${TRANSLATION_ROOT}.no-users-body`)}</p>
      </Modal>
    );
  }

  const response = mutation.data;
  const error = mutation.error;
  const isSuccess = response?.status === 'success';
  const isFailure = response?.status === 'failed';

  const inlineErrorKey: Partial<Record<string, string>> = {
    [ErrorCode.ENTRA_USERNAME_NOT_FOUND]: 'errors.username-not-found',
    [ErrorCode.ENTRA_USERNAME_AMBIGUOUS]: 'errors.username-ambiguous',
    [ErrorCode.INVALID_ARGUMENT]: 'errors.invalid-argument',
  };
  const inlineUsernameErrorKey = error?.code ? inlineErrorKey[error.code] : undefined;
  const inlineUsernameError = inlineUsernameErrorKey
    ? t(`${TRANSLATION_ROOT}.${inlineUsernameErrorKey}`)
    : undefined;

  const genericError = error && !inlineUsernameError ? error : undefined;
  const genericTraceId =
    typeof genericError?.data?.traceId === 'string' ? genericError.data.traceId : EMPTY;

  return (
    <Modal
      destroyOnHidden
      open={open}
      title={t(`${TRANSLATION_ROOT}.title`)}
      onCancel={handleClose}
      afterClose={handleAfterClose}
      onOk={() => form.submit()}
      okText={mutation.isPending ? t(`${TRANSLATION_ROOT}.testing`) : t(`${TRANSLATION_ROOT}.test`)}
      cancelText={t('common.close')}
      okButtonProps={{ loading: mutation.isPending, disabled: !canSubmit }}
      maskClosable={false}
      width={800}
    >
      <p className="test-entra-auth__intro">{t(`${TRANSLATION_ROOT}.intro`)}</p>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        className="test-entra-auth__form"
      >
        <Form.Item
          name="providedName"
          label={t(`${TRANSLATION_ROOT}.username-label`)}
          rules={[{ required: true, message: '' }]}
          validateStatus={inlineUsernameError ? 'error' : undefined}
          help={inlineUsernameError}
        >
          <Input
            autoFocus
            autoComplete="off"
            placeholder={t(`${TRANSLATION_ROOT}.username-placeholder`)}
            onPressEnter={handlePressEnter}
          />
        </Form.Item>
        <Form.Item
          name="password"
          label={t(`${TRANSLATION_ROOT}.password-label`)}
          rules={[{ required: true, message: '' }]}
        >
          <Input.Password
            autoComplete="off"
            placeholder={t(`${TRANSLATION_ROOT}.password-placeholder`)}
            onPressEnter={handlePressEnter}
          />
        </Form.Item>
      </Form>

      {genericError && (
        <Alert
          type="error"
          className="test-entra-auth__generic-error"
          message={t(`${TRANSLATION_ROOT}.errors.generic`)}
          description={
            <>
              <div className="test-entra-auth__error-field">
                <strong>{t(`${TRANSLATION_ROOT}.errors.trace-id-label`)}:</strong>{' '}
                {genericTraceId}
              </div>
              {genericError.type && (
                <div className="test-entra-auth__error-field">
                  <strong>{t(`${TRANSLATION_ROOT}.errors.type-label`)}:</strong> {genericError.type}
                </div>
              )}
              {genericError.code && (
                <div className="test-entra-auth__error-field">
                  <strong>{t(`${TRANSLATION_ROOT}.errors.code-label`)}:</strong> {genericError.code}
                </div>
              )}
              {genericError.message && (
                <div className="test-entra-auth__error-field">
                  <strong>{t(`${TRANSLATION_ROOT}.errors.message-label`)}:</strong>{' '}
                  {genericError.message}
                </div>
              )}
              <div className="test-entra-auth__actions">
                <CopyButton
                  textToCopy={buildErrorClipboardText(genericError, genericTraceId, t)}
                  dynamicLabel={{
                    initialLabel: t(`${TRANSLATION_ROOT}.copy`),
                    labelAfterClick: t('common.copied'),
                    dynamicLabelDuration: 3000,
                  }}
                />
              </div>
            </>
          }
        />
      )}

      {response && (
        <ResultPanel
          isSuccess={isSuccess}
          isFailure={isFailure}
          response={response}
          tenantId={tenantId}
        />
      )}
    </Modal>
  );
};

type ResultPanelProps = {
  isSuccess: boolean;
  isFailure: boolean;
  response: EntraIdAuthenticationTestResponse;
  tenantId: string;
};

const ResultPanel = ({ isSuccess, isFailure, response, tenantId }: ResultPanelProps) => {
  const { t } = useTranslation();
  const microsoftTrace = getMicrosoftTrace(response);
  const rows = identityRows(response.userIdentity, t);

  const variant = isSuccess ? 'ok' : 'err';

  return (
    <>
      <h4 className={`test-entra-auth__status test-entra-auth__status--${variant}`}>
        {t(`${TRANSLATION_ROOT}.result.summary`, {
          status: isSuccess
            ? t(`${TRANSLATION_ROOT}.result.success-heading`)
            : t(`${TRANSLATION_ROOT}.result.failure-heading`),
        })}
      </h4>
      <div className={`test-entra-auth__result test-entra-auth__result--${variant}`}>
        <div className="test-entra-auth__message">
          <MarkdownMessage source={response.results.message} />
        </div>

        <div className="test-entra-auth__details">
          <table>
            <tbody>
              <tr>
                <td>{t(`${TRANSLATION_ROOT}.trace.tenant`)}</td>
                <td>
                  <code>{tenantId}</code>
                </td>
              </tr>
              <tr>
                <td>{t(`${TRANSLATION_ROOT}.trace.impr`)}</td>
                <td>
                  <code>{response.traceId}</code>
                </td>
              </tr>
              {isFailure && (
                <tr>
                  <td>{t(`${TRANSLATION_ROOT}.trace.ms`)}</td>
                  <td>
                    <code>{microsoftTrace}</code>
                  </td>
                </tr>
              )}
              {rows.map(([k, v]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td>
                    <code>{v}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isFailure && response.results.rawOutput && (
          <Collapse
            className="test-entra-auth__raw"
            items={[
              {
                key: 'raw',
                label: t(`${TRANSLATION_ROOT}.raw-heading`),
                children: (
                  <pre>{JSON.stringify(response.results.rawOutput, null, 2)}</pre>
                ),
              },
            ]}
          />
        )}

        <div className="test-entra-auth__actions">
          <CopyButton
            textToCopy={buildClipboardText(response, tenantId, isFailure, t)}
            dynamicLabel={{
              initialLabel: isFailure
                ? t(`${TRANSLATION_ROOT}.copy`)
                : t(`${TRANSLATION_ROOT}.copy-success`),
              labelAfterClick: t('common.copied'),
              dynamicLabelDuration: 3000,
            }}
          />
        </div>
      </div>
    </>
  );
};

export default TestEntraAuthModal;
